import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Plus, Trash2, Edit2, Copy, Check, X, Save, Download, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Invoices({ embedded = false }) {
  const [invoices, setInvoices] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    items: [{ name: '', quantity: 1, unit_price: '', total: 0 }],
    tax: 0,
    notes: '',
    due_date: '',
    event_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [invoicesRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/standalone-invoices`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers })
      ]);
      
      setInvoices(invoicesRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = parseFloat(formData.tax) || 0;
    return subtotal + tax;
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto-calculate total
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].total = qty * price;
    }
    
    setFormData({...formData, items: newItems});
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { name: '', quantity: 1, unit_price: '', total: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.client_email) {
      toast.error('Please fill in client name and email');
      return;
    }

    const validItems = formData.items.filter(item => item.name && item.total > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const payload = {
      client_name: formData.client_name,
      client_email: formData.client_email,
      client_phone: formData.client_phone || null,
      items: validItems,
      subtotal: calculateSubtotal(),
      tax: parseFloat(formData.tax) || 0,
      total: calculateTotal(),
      notes: formData.notes || null,
      due_date: formData.due_date || null,
      event_id: formData.event_id || null
    };

    try {
      const token = localStorage.getItem('vyrus_token');
      if (editingInvoice) {
        await axios.put(`${BACKEND_URL}/api/standalone-invoices/${editingInvoice.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Invoice updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/standalone-invoices`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Invoice created');
      }
      
      setShowForm(false);
      setEditingInvoice(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save invoice');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/standalone-invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const markAsPaid = async (invoiceId, paymentMethod) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(`${BACKEND_URL}/api/standalone-invoices/${invoiceId}/status?status=paid&payment_method=${paymentMethod}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      client_name: invoice.client_name,
      client_email: invoice.client_email,
      client_phone: invoice.client_phone || '',
      items: invoice.items || [{ name: '', quantity: 1, unit_price: '', total: 0 }],
      tax: invoice.tax || 0,
      notes: invoice.notes || '',
      due_date: invoice.due_date || '',
      event_id: invoice.event_id || ''
    });
    setShowForm(true);
  };

  const copyInvoiceLink = (invoiceId) => {
    const link = `${window.location.origin}/invoice/${invoiceId}`;
    navigator.clipboard.writeText(link);
    toast.success('Invoice link copied!');
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_email: '',
      client_phone: '',
      items: [{ name: '', quantity: 1, unit_price: '', total: 0 }],
      tax: 0,
      notes: '',
      due_date: '',
      event_id: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const filteredInvoices = invoices
    .filter(inv => filter === 'all' || inv.status === filter)
    .filter(inv => 
      searchTerm === '' || 
      inv.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const stats = {
    total: invoices.length,
    unpaid: invoices.filter(i => i.status === 'unpaid').reduce((sum, i) => sum + (i.total || 0), 0),
    paid: invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.total || 0), 0)
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20">
      {!embedded && (
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight">Invoices</h1>
          <p className="text-muted-foreground mt-1 text-sm">Create and manage client invoices</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-card border-border/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Unpaid</p>
            <p className="text-xl font-bold text-yellow-500">{formatCurrency(stats.unpaid)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Collected</p>
            <p className="text-xl font-bold text-green-500">{formatCurrency(stats.paid)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <Input
          placeholder="Search by name or invoice #"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-xs bg-secondary/50 border-transparent"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
        >
          <option value="all">All</option>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
        </select>
        
        <Button onClick={() => { resetForm(); setShowForm(true); setEditingInvoice(null); }} className="ml-auto">
          <Plus className="h-4 w-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {/* Invoice List */}
      <div className="space-y-3">
        {filteredInvoices.length === 0 ? (
          <Card className="p-8 text-center bg-card border-border/50">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No invoices yet</p>
          </Card>
        ) : (
          filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="bg-card border-border/50">
              <CardContent className="p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{invoice.client_name}</span>
                        <span className="text-sm text-muted-foreground">{invoice.invoice_number}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          invoice.status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {invoice.status?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{invoice.client_email}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(invoice.created_at).toLocaleDateString()}
                        {invoice.items && ` • ${invoice.items.length} item(s)`}
                      </p>
                    </div>
                    <p className="text-xl font-bold text-primary font-mono">{formatCurrency(invoice.total)}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyInvoiceLink(invoice.id)}>
                      <Copy className="h-3 w-3 mr-1" /> Copy Link
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(invoice)}>
                      <Edit2 className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    {invoice.status === 'unpaid' && (
                      <>
                        <Button size="sm" className="bg-blue-500 hover:bg-blue-600" onClick={() => markAsPaid(invoice.id, 'zelle')}>
                          Zelle
                        </Button>
                        <Button size="sm" className="bg-gray-500 hover:bg-gray-600" onClick={() => markAsPaid(invoice.id, 'apple_pay')}>
                          Apple
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => markAsPaid(invoice.id, 'cash_app')}>
                          Cash App
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(invoice.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Payment Methods Reference */}
      <Card className="bg-secondary/30 border-border/30">
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-2">Payment Methods</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-blue-500">💳 Zelle:</span>
              <span className="text-muted-foreground">516-424-4367</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">🍎 Apple Pay:</span>
              <span className="text-muted-foreground">516-424-4367</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-500">💵 Cash App:</span>
              <span className="text-muted-foreground">$WallaceOz</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-card">
            <CardHeader className="border-b border-border flex-row items-center justify-between">
              <CardTitle>{editingInvoice ? 'Edit Invoice' : 'Create Invoice'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingInvoice(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Client Info */}
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground">Client Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Name *</label>
                      <Input
                        value={formData.client_name}
                        onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                        placeholder="Client name"
                        className="bg-secondary/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Email *</label>
                      <Input
                        type="email"
                        value={formData.client_email}
                        onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                        placeholder="email@example.com"
                        className="bg-secondary/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Phone</label>
                      <Input
                        value={formData.client_phone}
                        onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                        placeholder="Optional"
                        className="bg-secondary/50 border-transparent focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Due Date</label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                        className="bg-secondary/50 border-transparent focus:border-primary"
                      />
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm text-muted-foreground">Line Items</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                  
                  {formData.items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-end p-2 bg-secondary/20 rounded-lg">
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground mb-1 block">Item</label>
                        <Input
                          value={item.name}
                          onChange={(e) => updateItem(index, 'name', e.target.value)}
                          placeholder="Description"
                          className="bg-secondary/50 border-transparent focus:border-primary text-sm"
                        />
                      </div>
                      <div className="w-16">
                        <label className="text-xs text-muted-foreground mb-1 block">Qty</label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="bg-secondary/50 border-transparent focus:border-primary text-sm"
                        />
                      </div>
                      <div className="w-24">
                        <label className="text-xs text-muted-foreground mb-1 block">Price</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          placeholder="0.00"
                          className="bg-secondary/50 border-transparent focus:border-primary text-sm"
                        />
                      </div>
                      <div className="w-20 text-right">
                        <label className="text-xs text-muted-foreground mb-1 block">Total</label>
                        <p className="font-mono text-sm py-2">{formatCurrency(item.total)}</p>
                      </div>
                      {formData.items.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => removeItem(index)}>
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="space-y-2 border-t border-border pt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-mono">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <div className="relative w-24">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.tax}
                        onChange={(e) => setFormData({...formData, tax: e.target.value})}
                        className="pl-5 bg-secondary/50 border-transparent focus:border-primary text-sm h-8"
                      />
                    </div>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                    <span>Total</span>
                    <span className="text-primary font-mono">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Payment instructions, terms, etc."
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground resize-none text-sm"
                  />
                </div>

                {/* Event Link */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Link to Event (Optional)</label>
                  <select
                    value={formData.event_id}
                    onChange={(e) => setFormData({...formData, event_id: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground text-sm"
                  >
                    <option value="">No event</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.name}</option>
                    ))}
                  </select>
                </div>

                <Button type="submit" className="w-full">
                  <Save className="h-4 w-4 mr-2" /> {editingInvoice ? 'Update' : 'Create'} Invoice
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
